import React from 'react';
import { cn } from '../../lib/utils';
import Bee from '../bees/Bee';
import { HONEY, speciesForBot, speciesForBots } from '../bees/beeSvg';
import { useMyBotsQuery } from '../../hooks/queries';

const SIZES = { xs: 18, sm: 24, md: 36, lg: 44 };
const BEE_STATES = { working: 'work', needs_you: 'needs', failed: 'fail' };

// One species map per bots list (the sidebar's /bots/mine), so every avatar of a bot is
// the same bee and the person's bots differ while species are left, as in the desktop hive.
const speciesMaps = new WeakMap();
const speciesMapOf = (bots) => {
  if (!Array.isArray(bots)) return null;
  if (!speciesMaps.has(bots)) speciesMaps.set(bots, speciesForBots(bots));
  return speciesMaps.get(bots);
};

/** The bee a bot is: from the person's bots when they are loaded, else from its id. */
export const useBotSpecies = (bot) => {
  // Reads what the sidebar already loaded; never fetches by itself.
  const { data } = useMyBotsQuery({ enabled: false });
  // BusyBee itself, the main chat (backend agents/queen.py); a notification knows only her id.
  if (bot?.queen || (bot?.id && bot.id === data?.queen?.id)) return 'kralovna';
  return speciesMapOf(data?.bots)?.[bot?.id] || speciesForBot(bot);
};

/**
 * A bot's face: its bee in the bot's color (bots are colleagues; projects are squares).
 * The bee shows the state, after Grok Bot: it buzzes while it works, holds up an orange
 * mark when it needs the person and a red one when its work failed. A resting bee
 * stays still, so a list of bots stays calm.
 */
const BotAvatar = ({ bot, size = 'md', state = null, className }) => {
  const species = useBotSpecies(bot);
  const px = typeof size === 'number' ? size : SIZES[size] || SIZES.md;
  return (
    <span className={cn('relative inline-flex items-center justify-center shrink-0', className)} aria-hidden="true">
      <Bee species={species} color={species === 'kralovna' ? HONEY : bot?.avatar_color} state={BEE_STATES[state] || 'idle'}
        size={px} still tight />
    </span>
  );
};

/** The state a bot shows, from what /bots/mine reports. */
export const botState = (bot) => (bot?.pending_approvals > 0 ? 'needs_you' : bot?.working > 0 ? 'working' : null);

export default BotAvatar;
